import { TokenStore } from '../auth/TokenStore';
import { AuthService } from '../auth/AuthService';

export type CallbackParams = {
  code?: string;
  state?: string;
  error?: string;
  errorDescription?: string;
};

export type CallbackResult =
  | { ok: true; returnPath: string }
  | { ok: false; kind: 'NOT_AUTHORIZED'; message: string }
  | {
      ok: false;
      kind:
        | 'STATE_MISMATCH'
        | 'MISSING_CODE'
        | 'EXCHANGE_FAILED'
        | 'PKCE_MISSING'
        | 'PKCE_EXPIRED';
      message: string;
    };

const PENDING_LOGIN_MAX_AGE_MS = 10 * 60 * 1000; // 10 minutes

export function parseCallbackParams(callbackUrl: string): CallbackParams {
  const url = new URL(callbackUrl);
  return {
    code: url.searchParams.get('code') ?? undefined,
    state: url.searchParams.get('state') ?? undefined,
    error: url.searchParams.get('error') ?? undefined,
    errorDescription: url.searchParams.get('error_description') ?? undefined,
  };
}

export async function handleCallback(callbackUrl: string): Promise<CallbackResult> {
  const params = parseCallbackParams(callbackUrl);

  // Check for explicit error from Cognito (e.g., access denied, not authorized)
  if (params.error) {
    TokenStore.clearPendingLogin();
    return {
      ok: false,
      kind: 'NOT_AUTHORIZED',
      message: 'Not authorized',
    };
  }

  // Load pending login context
  const pending = TokenStore.getPendingLogin();
  if (!pending) {
    return { ok: false, kind: 'PKCE_MISSING', message: 'No pending login context found' };
  }

  // Validate state
  if (!params.state || params.state !== pending.state) {
    TokenStore.clearPendingLogin();
    return { ok: false, kind: 'STATE_MISMATCH', message: 'OAuth state mismatch' };
  }

  // Validate not expired
  if (Date.now() - pending.createdAt > PENDING_LOGIN_MAX_AGE_MS) {
    TokenStore.clearPendingLogin();
    return { ok: false, kind: 'PKCE_EXPIRED', message: 'Login session expired, please try again' };
  }

  // Ensure code exists
  if (!params.code) {
    TokenStore.clearPendingLogin();
    return { ok: false, kind: 'MISSING_CODE', message: 'No authorization code received' };
  }

  // Exchange code for tokens
  const exchangeResult = await AuthService.exchangeCodeForTokens(params.code, pending.verifier);
  if (!exchangeResult.ok) {
    TokenStore.clearPendingLogin();
    return { ok: false, kind: 'EXCHANGE_FAILED', message: 'Token exchange failed' };
  }

  // Store tokens and clear pending login
  TokenStore.setTokens(exchangeResult.tokens);
  TokenStore.clearPendingLogin();

  return { ok: true, returnPath: pending.returnPath };
}
